import React, { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials } from '../types';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { trackEvent, ANALYTICS_EVENTS } from '../services/analytics';

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<LoginCredentials>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string>('');
  const [rememberMe, setRememberMe] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginCredentials> = {};

    // Email validation
    if (!credentials.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!credentials.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setApiError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await login(credentials);
      trackEvent(ANALYTICS_EVENTS.LOGIN_SUCCESS);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const message = error.response?.data?.error?.message || 'Login failed. Please check your credentials.';
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof LoginCredentials, value: string): void => {
    setCredentials((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear API error when user makes changes
    if (apiError) {
      setApiError('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 w-full" noValidate>
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Email</label>
            <Input
              id="email"
              type="email"
              value={credentials.email}
              onChange={(e) => handleChange('email', e.target.value)}
              disabled={isSubmitting}
              placeholder="Enter your email"
              autoComplete="email"
              className={cn("h-12", errors.email && "border-red-500")}
            />
            {errors.email && <p className="text-sm font-medium text-red-500">{errors.email}</p>}
        </div>

        <div className="space-y-2">
            <label htmlFor="password" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]">Password</label>
            <Input
                 id="password"
                 type="password"
                 value={credentials.password}
                 onChange={(e) => handleChange('password', e.target.value)}
                 disabled={isSubmitting}
                 placeholder="Enter your password"
                 autoComplete="current-password"
                 className={cn("h-12", errors.password && "border-red-500")}
            />
             {errors.password && <p className="text-sm font-medium text-red-500">{errors.password}</p>}
        </div>
        
        <div className="flex items-center justify-between mt-1">
             <div className="flex items-center space-x-2">
                <input 
                    type="checkbox" 
                    id="remember" 
                    className="h-4 w-4 rounded border-gray-300 text-[#F16D34] focus:ring-[#F16D34]"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label
                    htmlFor="remember"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-[#161E54]"
                >
                    Remember me
                </label>
             </div>
             <Link to="/forgot-password" className="text-sm font-bold text-[#F16D34] hover:text-[#161E54] hover:underline underline-offset-4">Forgot password?</Link>
         </div>
      </div>

      {apiError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm animate-fade-in">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{apiError}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        size="lg"
        className="w-full font-bold text-lg uppercase tracking-wider h-14 bg-[#F16D34] hover:bg-[#E05D25] shadow-neo hover:shadow-none translate-x-[2px] translate-y-[2px] hover:translate-x-[4px] hover:translate-y-[4px] transition-all"
      >
        {isSubmitting ? (
             <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Signing in...
             </>
        ) : 'Sign in'}
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-gray-500">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full h-12 gap-2"
        onClick={() => {
          let url = import.meta.env.VITE_API_URL || 'http://localhost:8080';
          url = url.trim().replace(/\/+$/, '');
          if (url.endsWith('/api')) {
            url = url.slice(0, -4);
          }
          window.location.href = `${url}/api/auth/google/login`;
        }}
      >
        <svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        Sign in with Google
      </Button>
    </form>
  );
};

export default LoginForm;
